import React from 'react';
import styled, { css } from 'styled-components';
import { Button, ButtonText, Checkbox, Input, LoaderSpin, TabNav, TabNavItem, Text, Tooltip } from 'components';
import { JobTitleCreatableSelect } from 'components/JobTitleCreatableSelect/JobTitleCreatableSelect';
import { lineHeight, theme, toRem } from 'design';

export const JobsHeader = styled.div<{ $noActionButton?: boolean }>`
  width: ${({ $noActionButton }) => ($noActionButton ? '100%' : `calc(100% - ${toRem(32)})`)};
`;

export const StyledTitle = styled(Text)`
  flex-grow: 1;
  min-width: ${toRem(110)};
`;

export const StyledText = styled(Text)`
  flex-basis: ${toRem(100)};

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    flex-basis: ${toRem(70)};
  }
`;

export const DetailsText = styled(Text)`
  color: ${theme.color.neutral[500]};
`;

export const StyledRow = styled.div`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  @media (max-width: ${theme.breakpoint.med}px) {
    width: 100%;
  }
`;

export const ContributingCardsRow = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: ${toRem(theme.space.md)};
  width: 100%;

  & > * {
    flex: 1;
    min-width: ${toRem(280)};
  }
`;

export const Column = styled.div<{ gap?: number }>`
  flex-direction: column;
  width: 100%;
  gap: ${({ gap }) => toRem(gap ?? theme.space.sm)};
`;

export const JobTitleField = styled(JobTitleCreatableSelect)<{ $error?: boolean }>`
  min-width: ${toRem(100)};
  border-radius: ${toRem(5.5)};

  ${({ $error }) =>
    $error &&
    css`
      border: ${toRem(1)} solid ${theme.color.danger[50]};
      border-radius: ${toRem(5.5)};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      .react-dropdown-select-content {
        background-color: ${theme.color.white} !important;

        input {
          background-color: ${theme.color.white} !important;

          &::placeholder {
            color: ${theme.color.neutral[400]} !important;
          }
        }
      }
    `}
`;

export const PointsField = styled.input<{ $error: boolean; $keepStyles?: boolean }>`
  flex-basis: ${toRem(100)};
  flex-shrink: 0;
  text-align: center;
  border: ${({ $error }) => `${toRem(1)} solid ${!$error ? theme.color.neutral[200] : theme.color.danger[50]}`};
  border-radius: ${toRem(5.5)};
  height: ${toRem(45)};
  min-width: ${toRem(70)};

  ::-webkit-inner-spin-button,
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:hover {
    border-color: ${({ $error }) => (!$error ? theme.color.neutral[400] : theme.color.danger[50])};
  }

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    flex-shrink: 1;
  }

  &:disabled {
    background-color: ${({ $keepStyles }) => ($keepStyles ? theme.color.neutral[50] : theme.color.neutral[100])};

    &:hover {
      border-color: ${theme.color.neutral[200]};
    }
  }
`;

export const ErrorText = styled(Text)<{ $margin?: number }>`
  color: ${theme.color.danger[50]};
  margin-top: ${({ $margin }) => `${toRem($margin ?? theme.space.xs)}`};
`;

export const StyledButton = styled(Button)<{ $fitContent?: boolean }>`
  align-self: flex-start;

  ${({ $fitContent }) =>
    $fitContent &&
    css`
      width: fit-content !important;
      padding: 0 ${toRem(theme.space.lg)} !important;
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      color: ${theme.color.neutral[400]};
      background-color: ${theme.color.neutral[200]};

      &:hover,
      &:focus-visible {
        color: ${theme.color.neutral[400]};
        background-color: ${theme.color.neutral[200]};
      }
    `}
`;

export const StyledButtonText = styled(ButtonText)<{ appearance?: string; hideDesktop?: boolean }>`
  width: fit-content !important;
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  color: ${theme.color.primary[600]};
  text-decoration: none;

  &:hover {
    color: ${theme.color.primary[800]};
  }

  ${({ appearance }) =>
    appearance === 'destructive' &&
    css`
      color: ${theme.color.danger[50]};
    `}

  ${({ hideDesktop }) =>
    hideDesktop &&
    css`
      @media (min-width: ${theme.breakpoint.med}px) {
        display: none;
      }
    `}
`;

export const TipCalculationsDescription = styled(Text)`
  color: ${theme.color.neutral[600]};
`;

export const TipSharesText = styled(Text)<{ hideMobile?: boolean; hideDesktop?: boolean }>`
  color: ${theme.color.neutral[500]};
  min-width: ${toRem(12)};

  ${({ hideMobile }) =>
    hideMobile &&
    css`
      @media (max-width: ${theme.breakpoint.med}px) {
        display: none;
      }
    `}

  ${({ hideDesktop }) =>
    hideDesktop &&
    css`
      @media (min-width: ${theme.breakpoint.med}px) {
        display: none;
      }
    `}
`;

export const ModalInfo = styled(Text)<{ padding?: number }>`
  color: ${theme.color.neutral[500]};
  padding: ${({ padding }) => `0 ${toRem(padding ?? theme.space.sm)}`};
  text-align: center;

  @media (max-width: ${theme.breakpoint.med}px) {
    padding: 0 ${toRem(theme.space.sm)};
  }
`;

export const StyledTabNav = styled(TabNav)`
  flex-flow: row nowrap;
  justify-content: flex-start;
  margin-top: 0;

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    flex-direction: column;
    gap: ${toRem(theme.space.sm)};
  }
`;

export const StyledTabNavItem = styled(TabNavItem)`
  color: ${theme.color.neutral[800]};
  padding: ${toRem(theme.space.lg)} !important;
  width: auto !important;

  ${({ isActive }) =>
    isActive &&
    css`
      background-color: ${theme.color.secondary[400]};
      color: ${theme.color.white};
    `}
`;

export const DivWithGap = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: ${toRem(theme.space.xs)};

  @media (max-width: ${theme.breakpoint.small}px) {
    span {
      margin-bottom: ${toRem(theme.space.xs)};
    }
  }
`;

export const TipCalculatorRow = styled.div`
  flex-flow: row noWrap;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
  width: 100%;
`;

export const TipShareRow = styled.div`
  flex-flow: row noWrap;
  justify-content: center;
  align-items: flex-start;
  gap: ${toRem(theme.space.sm)};
  margin-bottom: ${toRem(theme.space.xs)};

  .shares-container {
    width: 20%;
  }
  .source-container {
    width: 82%;
  }
  .remainder-split-row {
    width: 45%;
  }

  @media (max-width: ${theme.breakpoint.med}px) {
    flex-wrap: wrap;
    background-color: ${theme.color.neutral[100]};
    padding: ${toRem(theme.space.md)};
    border-radius: ${toRem(8)};

    .shares-container {
      width: 100%;
    }
    .source-container {
      width: 100%;
    }
    .remainder-split-row {
      width: 100%;
      .split-container {
        max-width: ${toRem(60)};
      }
    }
  }
`;

export const FieldContainer = styled.div`
  flex-flow: row nowrap;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
  flex-grow: 1;
  width: 100%;

  @media (max-width: ${theme.breakpoint.med}px) {
    flex-wrap: wrap;
  }
`;

export const TipCalculatorFieldsContainer = styled.div<{ fullWidth?: boolean }>`
  flex-direction: column;
  gap: ${toRem(theme.space.sm)};
  flex-grow: 1;

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
`;

export const TipShareFormContainer = styled.div`
  flex-direction: column;
  padding: ${toRem(theme.space.xl)};
  gap: ${toRem(theme.space.xl)};
  background: ${theme.color.neutral[50]};
  border-radius: ${toRem(8)};
  margin-top: ${toRem(theme.space.xl)};
  width: 100%;

  .selector {
    min-width: ${toRem(120)};
  }

  @media (max-width: ${theme.breakpoint.small}px) {
    padding: ${toRem(theme.space.md)};
  }
`;

export const SalesGroupFormContainer = styled(TipShareFormContainer)`
  margin: ${toRem(theme.space.xl)} ${toRem(theme.space.xl)} 0;
  background: ${theme.color.neutral[100]};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
`;

export const ModalWarning = styled.div`
  flex-direction: column;
  width: 100%;
  margin-left: ${toRem(theme.space.xl)};

  p {
    text-align: left;
  }

  ul {
    margin: ${toRem(theme.space.sm)} ${toRem(theme.space.sm)} ${toRem(theme.space.sm)} ${toRem(theme.space.xl)};
  }

  .title {
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
  }
`;

export const ButtonContainer = styled.div<{ isModal?: boolean; $noMargin?: boolean }>`
  flex-flow: row wrap;
  gap: ${toRem(theme.space.md)};
  align-self: normal;
  margin: ${({ $noMargin }) => ($noMargin ? 0 : `${toRem(theme.space['2xl'])} 0 0`)};

  ${({ isModal }) =>
    isModal &&
    css`
      flex-flow: row nowrap;
      justify-content: center;

      button {
        width: 100%;
        max-width: ${toRem(194.5)};
        flex-shrink: 1;
      }
    `}
`;

export const NumberInput = styled(Input)<{ $width?: number; $error?: boolean }>`
  min-width: ${({ $width }) => toRem($width || 80)};
  border: ${({ $error }) => `${toRem(1)} solid ${!$error ? theme.color.neutral[200] : theme.color.danger[50]}`};

  &:hover,
  &:focus-visible {
    border-color: ${({ $error }) => (!$error ? theme.color.neutral[400] : theme.color.danger[50])};
  }

  &:disabled {
    background-color: ${theme.color.white};

    &:hover,
    &:focus {
      background-color: ${theme.color.white};
    }
  }
`;

export const ModalContent = styled.div<{ fullWidth?: boolean }>`
  text-align: left;
  flex-direction: column;

  .points {
    margin-top: ${toRem(theme.space.xl)};
  }

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
`;

export const StyledDiv = styled.div`
  width: 100%;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
  margin-bottom: ${toRem(theme.space.sm)};

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    label {
      font-size: ${toRem(theme.font.size.sm)};
      line-height: ${lineHeight(theme.font.size.sm)};
    }
  }
`;

export const StyledCheckbox = styled(Checkbox)`
  margin-right: ${toRem(theme.space.xs)};
  width: fit-content;

  label {
    color: ${theme.color.neutral[900]};
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
    font-weight: ${theme.font.weight.medium};
  }

  input {
    margin-right: ${toRem(theme.space.sm)};
  }
`;

export const StyledSpinner = styled(LoaderSpin)`
  margin: ${toRem(theme.space.lg)} 0;
`;

export const LoadingText = styled(Text)`
  margin-top: ${toRem(theme.space.sm)};
  margin-bottom: ${toRem(theme.space.xl)};
  text-align: center;
`;

export const MoreInfoButton = styled.a`
  font-weight: ${theme.font.weight.medium};
  color: ${theme.color.primary[600]};
  text-decoration: none !important;

  &:hover,
  &:focus {
    text-decoration-line: underline !important;
  }
`;

interface IRemoveRuleTooltip {
  id: string;
  editPermission?: boolean;
  disabled?: boolean;
  featureUnavailable?: boolean;
}

export const RemoveRuleTooltip = ({ id, editPermission, disabled, featureUnavailable }: IRemoveRuleTooltip) => {
  let tooltipContent;

  if (!editPermission) {
    tooltipContent = (
      <>
        You do not have permission to edit job titles.
        <br />
        Contact your supervisor for more information.
      </>
    );
  } else if (featureUnavailable) {
    tooltipContent = (
      <>
        Editing calculator settings is temporarily unavailable.
        <br />
        Please try again later.
      </>
    );
  } else if (disabled) {
    tooltipContent = 'Jobs associated with a tip share or default pool cannot be removed.';
  }
  return tooltipContent ? <Tooltip id={id}>{tooltipContent}</Tooltip> : null;
};

export const PermissionTooltip = ({ type, id }: { type: string; id?: string }) => (
  <Tooltip id={id || `no-${type}-permission`}>
    You do not have permission to {type} calculator settings.
    <br />
    Contact your supervisor for more information.
  </Tooltip>
);

export const FeatureUnavailableTooltip = ({ id = 'feature-unavailable' }: { id?: string }) => (
  <Tooltip id={id}>
    Editing calculator settings is temporarily unavailable.
    <br />
    Please try again later.
  </Tooltip>
);

interface ISaveButton {
  isProcessing?: boolean;
  disabled?: boolean;
  editPermission?: boolean;
  featureUnavailable?: boolean;
}

export const SaveButton = ({ isProcessing, disabled, editPermission, featureUnavailable }: ISaveButton) => (
  <>
    <StyledButton
      type="submit"
      label="Save Settings"
      data-cy="save-settings-button"
      size="small"
      isProcessing={isProcessing}
      disabled={!editPermission || disabled}
      tooltipId={!editPermission ? 'no-edit-permission' : 'feature-unavailable'}
    />
    {!editPermission ? <PermissionTooltip type="edit" /> : featureUnavailable && <FeatureUnavailableTooltip />}
  </>
);
